import { ChangeDetectorRef, Component } from "@angular/core";
import { ExcelStyle, GridApi, GridOptions, RowNode, SelectionChangedEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { ApiService } from "../../services";
import { LoadingScreenService } from "../../shared/loading-screen.service";
import { PlansAllRegionalPlanColumnDefinition } from "./ag-grid-column-definition";
import { GridViewBaseComponent } from "../../utility/grid-view-base.component";
import { SharedDataService } from "../../services/sharedDataService";
import { PlanKindEnum, PlanRegionStatusEnum } from "src/app/shared/enums/plan-enums";
import { extendDefaultOptions } from "../../utility/gridHelper";
import { exportExcel, getExportedExcelFileNameSuffix } from "src/app/utility/common-helper";
import { ModalService } from "src/app/shared/modal.service";
import { PlanRegionWithApprovalStatus } from "../../generated-models/PlanRegionWithApprovalStatus";

@Component({
  standalone: false,
  templateUrl: "./plan-all-regional-plans.component.html",
  styleUrls: ["../../styles.scss", "./plan.scss"],
})
export class PlansAllRegionalPlansComponent extends GridViewBaseComponent {
  gridOptions: GridOptions;
  gridApi: GridApi;
  initPageSize: number = 15;
  isAdminOrOperator: boolean = false;
  allPlans: PlanRegionWithApprovalStatus[] = [];
  rowData: PlanRegionWithApprovalStatus[] = [];
  showLatestOnly = false;
  hasRowSelected = false;
  keyFilterStats: string = "filter:all-regional-plans";
  headerStyle: ExcelStyle[] = [
    {
      id: "header",
      font: {
        bold: true,
      },
    },
  ];

  constructor(
    apiService: ApiService,
    private loadingService: LoadingScreenService,
    protected sharedDataService: SharedDataService,
    private modalService: ModalService,
    private notificationService: ToastrService,
    private cdr: ChangeDetectorRef
  ) {
    super(apiService, sharedDataService);
  }

  ngOnInit() {
    this.gridOptions = extendDefaultOptions({
      columnDefs: PlansAllRegionalPlanColumnDefinition,
      paginationPageSize: this.initPageSize,
      isRowSelectable: (node: RowNode) => {
        const row = node.data;
        return (
          row.Status === PlanRegionStatusEnum.Approvable ||
          row.Status === PlanRegionStatusEnum.Created ||
          // Backend enforces admin-only removal of Error status plans.
          row.Status === PlanRegionStatusEnum.Error
        );
      },
      enableRangeSelection: false,
      rowSelection: "multiple",
      animateRows: true,
    });
    this.apiService.getUserProfile().then((response) => {
      this.userProfile = response;
      this.loadingService.setLoading(true);

      if (this.userProfile?.IsAdmin || this.userProfile?.IsOperator) {
        this.isAdminOrOperator = true;
        PlansAllRegionalPlanColumnDefinition[0].checkboxSelection = (params) =>
          params.data.Status === PlanRegionStatusEnum.Approvable ||
          params.data.Status === PlanRegionStatusEnum.Created ||
          // Backend enforces admin-only removal of Error status plans.
          params.data.Status === PlanRegionStatusEnum.Error;
        PlansAllRegionalPlanColumnDefinition[0].headerCheckboxSelection = true;
        const colDefs = PlansAllRegionalPlanColumnDefinition;
        this.gridApi?.setGridOption('columnDefs', colDefs);
      }

      this.getAllRegionalPlans();
    });
  }

  getAllRegionalPlans() {
    this.apiService.getRegionalPlans(null, true).subscribe(
      (response) => {
        response.forEach((plan) => {
          if (!plan.Kind && plan.Blueprint.includes("_")) {
            plan.Kind = PlanKindEnum.Growth;
          }
        });

        this.allPlans = response;
        this.applyLatestFilter();
        this.loadingService.setLoading(false);
        setTimeout(() => {
          this.gridApi.autoSizeAllColumns();
        }, 100);
        super.setupFilterByLocalData();
      },
      (e: any) => {
        this.allPlans = [];
        this.rowData = [];
        this.loadingService.setLoading(false);
        this.notificationService.error(e);
      }
    );
  }

  onShowLatestOnlyChanged(): void {
    this.gridApi?.deselectAll();
    this.hasRowSelected = false;
    this.applyLatestFilter();
  }

  private applyLatestFilter(): void {
    this.rowData = this.showLatestOnly ? this.computeLatestPlans(this.allPlans) : this.allPlans;
    this.gridApi?.setGridOption("rowData", this.rowData);
  }

  // Per ServiceTreeId + Region + Blueprint: latest Approved/Ready plan and latest non-Removed plan, de-duped.
  private computeLatestPlans(plans: PlanRegionWithApprovalStatus[]): PlanRegionWithApprovalStatus[] {
    const approvedStatuses: string[] = [PlanRegionStatusEnum.Approved, PlanRegionStatusEnum.Ready];
    const groups = new Map<string, PlanRegionWithApprovalStatus[]>();
    for (const plan of plans) {
      const groupKey = `${plan.ServiceTreeId}|${plan.Region}|${plan.Blueprint}`;
      const group = groups.get(groupKey);
      if (group) {
        group.push(plan);
      } else {
        groups.set(groupKey, [plan]);
      }
    }

    const result = new Map<string, PlanRegionWithApprovalStatus>();
    for (const group of groups.values()) {
      const latestApproved = this.getLatestVersion(group.filter((plan) => approvedStatuses.includes(plan.Status)));
      const latestNotRemoved = this.getLatestVersion(group.filter((plan) => plan.Status !== PlanRegionStatusEnum.Removed));
      for (const plan of [latestApproved, latestNotRemoved]) {
        if (plan) {
          result.set(`${plan.ServiceTreeId}|${plan.Region}|${plan.Blueprint}|${plan.Version}`, plan);
        }
      }
    }

    return [...result.values()];
  }

  private getLatestVersion(plans: PlanRegionWithApprovalStatus[]): PlanRegionWithApprovalStatus | undefined {
    return plans.reduce<PlanRegionWithApprovalStatus | undefined>(
      (latest, plan) => (!latest || plan.Version > latest.Version ? plan : latest),
      undefined
    );
  }

  onSelectionChanged(params: SelectionChangedEvent): void {
    this.hasRowSelected = params.api.getSelectedRows().length > 0;
    this.cdr.detectChanges();
  }

  refreshData() {
    this.loadingService.setLoading(true);
    this.getAllRegionalPlans();
  }

  exportExcel() {
    var fileName = "AllExecutionPlans-" + getExportedExcelFileNameSuffix();
    var sheetName = "All Execution Plans";
    this.loadingService.setLoading(true);
    exportExcel(this.gridApi, fileName, sheetName);
    this.loadingService.setLoading(false);
  }

  async batchRemovePlans() {
    const selectedRows = this.gridApi.getSelectedRows() as PlanRegionWithApprovalStatus[];
    await this.modalService.planSetRemovedModal(selectedRows);
    this.refreshData();
    this.hasRowSelected = false;
  }
}
